import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { WRITE_PERMISSIONS } from "@/lib/constants";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=not auth, obj=auth

  const refetch = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const login = useCallback(async (email, password) => {
    try {
      await api.post("/auth/login", { email, password });
      await refetch();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, [refetch]);

  const signup = useCallback(async (payload) => {
    try {
      await api.post("/auth/signup", payload);
      await refetch();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, [refetch]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  }, []);

  const hasWrite = useCallback((resource) => {
    if (!user) return false;
    if (user.role === "system_admin") return true;
    return (WRITE_PERMISSIONS[resource] || []).includes(user.role);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, refetch, hasWrite }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
